import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { FieldSearchSelect, FieldSelect } from "components/AtomsV2";

import { createChartXAxisLabel } from "utils/charts";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetEMSMemberEnergyQuery } from "state/communities/communitiesApiSlice";

import EmsChartCard from "./EmsChartCard";

const ALL_CPES = "__all__";

const MODE_NET = "net";
const MODE_DISAGGREGATED = "disaggregated";

const DISAGG_FIELDS = [
  { key: "e_sup", color: "var(--accent)", sign: 1 },
  { key: "e_sur", color: "var(--info)", sign: -1 },
  { key: "e_pur", color: "#7928CA", sign: 1 },
  { key: "e_sale", color: "var(--success)", sign: -1 },
];

function MemberEnergyChart({ moduleType, startDatetime, endDatetime, onDataStatus }) {
  const { t } = useTranslation(["community"]);
  const community = useSelector(selectCurrentCommunity);
  const communityId = community?.id;

  const { data: rawList = [], isLoading } = useGetEMSMemberEnergyQuery(
    {
      communityId,
      moduleType,
      startDatetime,
      endDatetime,
      limit: startDatetime || endDatetime ? 2000 : 1000,
      offset: 0,
    },
    { skip: !communityId, pollingInterval: 30000 }
  );

  const isEmpty = !isLoading && rawList.length === 0;

  useEffect(() => {
    if (!isLoading && onDataStatus) onDataStatus(!isEmpty);
  }, [isLoading, isEmpty, onDataStatus]);

  const [mode, setMode] = useState(MODE_NET);
  const [selectedCpe, setSelectedCpe] = useState(null);

  const cpes = useMemo(() => {
    const map = new Map();
    rawList.forEach((r) => {
      if (r.cpe_id && !map.has(r.cpe_id)) map.set(r.cpe_id, r.cpe_id);
    });
    return [...map.keys()];
  }, [rawList]);

  const effectiveCpe = selectedCpe || (cpes.length > 1 ? ALL_CPES : cpes[0]);

  const aggregated = useMemo(() => {
    if (rawList.length === 0) return new Map();
    const filtered =
      effectiveCpe === ALL_CPES ? rawList : rawList.filter((r) => r.cpe_id === effectiveCpe);
    const byTs = new Map();
    filtered.forEach((r) => {
      const key = r.datetime_timestamp;
      if (!byTs.has(key)) byTs.set(key, { e_sup: 0, e_sur: 0, e_pur: 0, e_sale: 0, e_cmet: 0 });
      const entry = byTs.get(key);
      [...DISAGG_FIELDS.map((f) => f.key), "e_cmet"].forEach((f) => {
        entry[f] = (entry[f] ?? 0) + (r[f] ?? 0);
      });
    });
    return byTs;
  }, [rawList, effectiveCpe]);

  const { series, labels, legend } = useMemo(() => {
    if (aggregated.size === 0) return { series: [], labels: [], legend: [] };
    const sorted = [...aggregated.entries()].sort(([a], [b]) => new Date(a) - new Date(b));
    const lbls = sorted.map(([ts]) => createChartXAxisLabel(ts, "MIN15"));

    if (mode === MODE_NET) {
      const netSeries = [
        {
          name: t("unified_ems.member_energy_e_cmet"),
          data: sorted.map(([, v]) => Number(v.e_cmet.toFixed(4))),
          color: "var(--warning)",
        },
      ];
      return {
        series: netSeries,
        labels: lbls,
        legend: netSeries.map((s) => ({ label: s.name, color: s.color })),
      };
    }

    const disaggSeries = DISAGG_FIELDS.map(({ key: f, color, sign }) => ({
      name: t(`unified_ems.member_energy_${f}`),
      data: sorted.map(([, v]) => Number((sign * Math.abs(v[f])).toFixed(4))),
      color,
    }));
    return {
      series: disaggSeries,
      labels: lbls,
      legend: disaggSeries.map((s) => ({ label: s.name, color: s.color })),
    };
  }, [aggregated, mode, t]);

  if (isLoading || isEmpty) return null;

  const toolbar = (
    <>
      <FieldSelect
        value={mode}
        onChange={(v) => setMode(v)}
        options={[
          { value: MODE_NET, label: t("unified_ems.member_energy_mode_net") },
          { value: MODE_DISAGGREGATED, label: t("unified_ems.member_energy_mode_disaggregated") },
        ]}
      />
      {cpes.length > 1 && (
        <FieldSearchSelect
          value={effectiveCpe}
          onChange={(v) => setSelectedCpe(v)}
          options={[
            { value: ALL_CPES, label: t("unified_ems.chart_community_aggregate") },
            ...cpes.map((id) => ({ value: id, label: id })),
          ]}
        />
      )}
    </>
  );

  return (
    <EmsChartCard
      title={t("unified_ems.member_energy_chart_title")}
      legend={legend}
      toolbar={toolbar}
      series={series}
      labels={labels}
      unit="kWh"
      type="bar"
    />
  );
}

MemberEnergyChart.defaultProps = {
  startDatetime: undefined,
  endDatetime: undefined,
  onDataStatus: undefined,
};

MemberEnergyChart.propTypes = {
  moduleType: PropTypes.string.isRequired,
  startDatetime: PropTypes.string,
  endDatetime: PropTypes.string,
  onDataStatus: PropTypes.func,
};

export default MemberEnergyChart;
